// src/modules/gerenciador/MateriasDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, paths } from '../../firebase/config';
import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  collection,
} from "firebase/firestore";
import { useAuth, logAuditoria } from '../../context/AuthContext';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { LoadingSpinner } from '../../components/LoadingSpinner';

// Ícones
const EditIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>;
const DeleteIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const EventIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 00-2-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;

export const MateriasDashboard = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Dados
  const [materias, setMaterias] = useState([]);
  const [cursos, setCursos] = useState([]);

  // Estados de Interface
  const [activeCursoTab, setActiveCursoTab] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'curso', 'materia' ou 'evento'
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);
  
  const [formData, setFormData] = useState({});
  const [currentItem, setCurrentItem] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // Estado para controlar o menu dropdown
  const [actionMenuOpenId, setActionMenuOpenId] = useState(null);

  useEffect(() => {
    setLoading(true);
    
    // Carrega Matérias e Eventos
    const unsubMaterias = onSnapshot(collection(db, paths.materias), (snap) => {
        const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setMaterias(lista.sort((a, b) => a.nome.localeCompare(b.nome)));
    }, (err) => {
        console.error("Erro ao carregar matérias:", err);
    });

    // Carrega Cursos
    const unsubCursos = onSnapshot(collection(db, paths.cursos), (snap) => {
        const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const sorted = lista.sort((a,b) => a.nome.localeCompare(b.nome));
        setCursos(sorted);
        
        if (sorted.length > 0 && !activeCursoTab) {
            setActiveCursoTab(sorted[0].id);
        }
    });
    
    setLoading(false);
    return () => { unsubMaterias(); unsubCursos(); };
  }, [activeCursoTab]);

  // Fecha o menu se clicar fora
  useEffect(() => {
      const handleClickOutside = (event) => {
          if (!event.target.closest('.action-menu-container')) {
              setActionMenuOpenId(null);
          }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const materiasDoCursoAtivo = useMemo(() => {
      if (!activeCursoTab) return [];
      return materias.filter(m => m.curso_id === activeCursoTab);
  }, [materias, activeCursoTab]);

  // Handler do Menu
  const toggleActionMenu = (id, e) => {
      e.stopPropagation();
      setActionMenuOpenId(actionMenuOpenId === id ? null : id);
  };

  // --- Handlers de Modal ---

  const handleOpenModal = (type, item = null) => {
    setModalType(type);
    setCurrentItem(item);
    setError('');
    setProcessing(false);
    
    if (type === 'curso') {
        setFormData(item ? { nome: item.nome } : { nome: '' });
    } else if (type === 'materia' || type === 'evento') {
        const cursoIdInicial = item ? item.curso_id : activeCursoTab;
        setFormData({ 
            nome: item ? item.nome : '', 
            carga_horaria: item ? item.carga_horaria : '', 
            curso_id: cursoIdInicial || '' 
        });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
      if (e) e.preventDefault();
      setProcessing(true);
      setError('');
      
      try {
          if (modalType === 'curso') {
              if (!formData.nome || !formData.nome.trim()) throw new Error("O nome do curso é obrigatório.");
              
              if (currentItem) {
                  await updateDoc(doc(db, paths.cursos, currentItem.id), { nome: formData.nome });
                  await logAuditoria(userProfile, "Atualização Curso", { id: currentItem.id, nome: formData.nome });
              } else {
                  const ref = doc(collection(db, paths.cursos));
                  await setDoc(ref, { id: ref.id, nome: formData.nome });
                  await logAuditoria(userProfile, "Criação Curso", { id: ref.id, nome: formData.nome });
                  if (cursos.length === 0) setActiveCursoTab(ref.id);
              }

          } else if (modalType === 'materia' || modalType === 'evento') {
              
              // --- VALIDAÇÃO ---
              if (!formData.nome || !formData.nome.trim()) {
                  throw new Error(`O nome d${modalType === 'evento' ? 'o evento' : 'a matéria'} é obrigatório.`);
              }
              
              // Só exige Carga Horária se for MATÉRIA
              if (modalType === 'materia') {
                  if (!formData.carga_horaria || !String(formData.carga_horaria).trim()) {
                      throw new Error("A carga horária é obrigatória para matérias.");
                  }
              }

              const cursoIdFinal = formData.curso_id || activeCursoTab;
              if (!cursoIdFinal) {
                  throw new Error("É obrigatório vincular a um Curso (Turma).");
              }
              // -----------------

              const cursoObj = cursos.find(c => c.id === cursoIdFinal);
              const cursoNome = cursoObj ? cursoObj.nome : 'N/D';

              const payload = {
                  nome: formData.nome,
                  // Se for evento, salva carga horária como vazio ou 0
                  carga_horaria: modalType === 'evento' ? '' : formData.carga_horaria,
                  curso_id: cursoIdFinal,
                  curso_nome: cursoNome,
                  tipo: modalType // 'materia' ou 'evento'
              };

              if (currentItem) {
                  await updateDoc(doc(db, paths.materias, currentItem.id), payload);
                  await logAuditoria(userProfile, "Atualização Matéria/Evento", { id: currentItem.id, nome: payload.nome, tipo: modalType });
              } else {
                  const newDocRef = doc(collection(db, paths.materias));
                  const dataToSave = { 
                      id: newDocRef.id, 
                      ...payload 
                  };
                  await setDoc(newDocRef, dataToSave);
                  await logAuditoria(userProfile, "Criação Matéria/Evento", { id: newDocRef.id, nome: payload.nome, tipo: modalType });
              }
          }
          setShowModal(false);
      } catch (err) {
          console.error("Erro ao salvar:", err);
          setError(err.message);
      } finally {
          setProcessing(false);
      }
  };

  const handleConfirmDelete = (type, id) => {
    if (type === 'curso') {
        const temMateria = materias.some(m => m.curso_id === id);
        if (temMateria) {
            alert("Não é possível excluir este curso pois existem matérias/eventos vinculados a ele. Exclua-os primeiro.");
            return;
        }
    }
    setDeleteAction({ type, id });
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    if (!deleteAction) return;
    setProcessing(true);
    try {
        const { type, id } = deleteAction;
        const collectionPath = type === 'curso' ? paths.cursos : paths.materias;
        await deleteDoc(doc(db, collectionPath, id));
        await logAuditoria(userProfile, `Exclusão ${type === 'curso' ? 'Curso' : 'Item da Grade'}`, { id });
        
        if (type === 'curso' && activeCursoTab === id) {
            setActiveCursoTab('');
        }
        setShowConfirm(false);
    } catch (err) { 
        setError(err.message); 
    } finally {
        setProcessing(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <header className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
            <img src="https://apmbb.com.br/resultados/brasao.png" alt="Brasão APMBB" className="h-12 w-12" />
            <div>
                <h1 className="text-3xl font-bold text-gray-800">Gestão Acadêmica</h1>
                <p className="text-sm text-gray-600">Gerencie os Cursos e suas respectivas Grades Curriculares.</p>
            </div>
        </div>
      </header>

      {error && !showModal && (<div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex justify-between"><span>{error}</span><button onClick={() => setError('')}>x</button></div>)}

      <div className="bg-white rounded-lg shadow p-4 animate-fade-in">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 border-b pb-4 gap-4">
              <h3 className="text-xl font-bold text-gray-800">Grade Curricular</h3>
              <div className="flex gap-3">
                  <Button onClick={() => handleOpenModal('curso')} variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                      + Novo Curso
                  </Button>
                  <Button onClick={() => handleOpenModal('materia')} variant="success">
                      + Nova Matéria
                  </Button>
                  <Button onClick={() => handleOpenModal('evento')} className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1">
                      <EventIcon /> Novo Evento
                  </Button>
              </div>
          </div>
          
          <div className="mb-6 overflow-x-auto pb-2">
              <nav className="flex space-x-2">
                  {cursos.map(c => (
                      <div key={c.id} className="relative group">
                          <button 
                              onClick={() => setActiveCursoTab(c.id)} 
                              className={`px-4 py-2 rounded-t-lg text-sm font-bold whitespace-nowrap transition-colors pr-8 ${
                                  activeCursoTab === c.id 
                                  ? 'bg-blue-600 text-white border-b-4 border-blue-800' 
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                          >
                              {c.nome}
                          </button>
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleConfirmDelete('curso', c.id); }}
                            className={`absolute right-1 top-2 text-xs font-bold px-1.5 rounded-full hover:bg-red-500 hover:text-white transition-colors ${activeCursoTab === c.id ? 'text-blue-200' : 'text-gray-400'}`}
                            title="Excluir Curso"
                          >
                            &times;
                          </button>
                      </div>
                  ))}
                  {cursos.length === 0 && <p className="text-sm text-gray-500 py-2">Nenhum curso cadastrado.</p>}
              </nav>
          </div>

          {activeCursoTab ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                      <h4 className="font-bold text-gray-700">Matérias de: {cursos.find(c => c.id === activeCursoTab)?.nome}</h4>
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">{materiasDoCursoAtivo.length} Itens</span>
                  </div>
                  
                  <div className="overflow-x-auto" style={{ minHeight: '300px' }}>
                      <table className="min-w-full text-sm">
                          <thead className="bg-white border-b">
                              <tr>
                                  <th className="px-4 py-3 text-left w-1/2 text-gray-500 font-medium">Nome</th>
                                  <th className="px-4 py-3 text-left w-1/4 text-gray-500 font-medium">Tipo</th>
                                  <th className="px-4 py-3 text-left w-1/4 text-gray-500 font-medium">Carga Horária</th>
                                  <th className="px-4 py-3 text-right text-gray-500 font-medium">Ações</th>
                              </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                              {materiasDoCursoAtivo.map(m => (
                                  <tr key={m.id} className="hover:bg-blue-50 transition-colors">
                                      <td className="px-4 py-3 font-medium text-gray-900">{m.nome}</td>
                                      <td className="px-4 py-3">
                                          {m.tipo === 'evento' 
                                            ? <span className="bg-purple-100 text-purple-800 text-xs font-bold px-2 py-1 rounded">Evento</span> 
                                            : <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded">Matéria</span>}
                                      </td>
                                      <td className="px-4 py-3 text-gray-600">{m.tipo === 'evento' ? '-' : (m.carga_horaria || '-')}</td>
                                      
                                      <td className="px-4 py-3 text-right relative action-menu-container">
                                          <button
                                              onClick={(e) => toggleActionMenu(m.id, e)}
                                              className="text-gray-600 hover:text-blue-600 font-bold text-sm flex items-center justify-end gap-1 ml-auto border border-gray-300 rounded px-3 py-1.5 bg-white hover:bg-gray-50 shadow-sm transition-all"
                                          >
                                              <EditIcon /> Editar
                                          </button>

                                          {actionMenuOpenId === m.id && (
                                              <div className="absolute right-0 top-10 w-32 bg-white rounded-md shadow-xl border border-gray-200 z-50 overflow-hidden ring-1 ring-black ring-opacity-5 animate-fade-in-down">
                                                  <button
                                                      onClick={() => { handleOpenModal(m.tipo || 'materia', m); setActionMenuOpenId(null); }}
                                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                                                  >
                                                      <EditIcon /> Editar
                                                  </button>
                                                  <button
                                                      onClick={() => { handleConfirmDelete('materia', m.id); setActionMenuOpenId(null); }}
                                                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 border-t border-gray-100 transition-colors"
                                                  >
                                                      <DeleteIcon /> Excluir
                                                  </button>
                                              </div>
                                          )}
                                      </td>
                                  </tr>
                              ))}
                              {materiasDoCursoAtivo.length === 0 && (
                                  <tr>
                                      <td colSpan="4" className="px-4 py-8 text-center text-gray-400 italic">
                                          Nenhum item cadastrado neste curso.
                                      </td>
                                  </tr>
                              )}
                          </tbody>
                      </table>
                  </div>
              </div>
          ) : (
              <div className="text-center py-10 text-gray-400 border-2 border-dashed rounded-lg">
                  {cursos.length === 0 ? "Cadastre um CURSO para começar." : "Selecione um Curso acima."}
              </div>
          )}
      </div>

      <Modal show={showModal} onClose={() => setShowModal(false)} title={modalType === 'curso' ? (currentItem ? 'Editar Curso' : 'Novo Curso') : (currentItem ? `Editar ${modalType === 'evento' ? 'Evento' : 'Matéria'}` : `Nov${modalType === 'evento' ? 'o Evento' : 'a Matéria'}`)} size="md">
          <form onSubmit={handleSubmit} className="space-y-4">
              
              {modalType === 'curso' && (
                  <div>
                      <label className="block text-sm font-medium text-gray-700">Nome do Curso</label>
                      <input 
                          value={formData.nome || ''} 
                          onChange={e => setFormData({...formData, nome: e.target.value})} 
                          className="w-full border p-2 rounded" 
                          required 
                          placeholder="Ex: QOPM - 1º CFO" 
                      />
                  </div>
              )}

              {(modalType === 'materia' || modalType === 'evento') && (
                  <>
                      <div>
                          <label className="block text-sm font-medium text-gray-700">
                              Nome d{modalType === 'evento' ? 'o Evento' : 'a Matéria'} *
                          </label>
                          <input 
                              value={formData.nome || ''} 
                              onChange={e => setFormData({...formData, nome: e.target.value})} 
                              className="w-full border p-2 rounded" 
                              required 
                              placeholder={modalType === 'evento' ? "Ex: Palestra Magna" : "Ex: Criminologia"} 
                          />
                      </div>
                      
                      {/* Carga Horária: SÓ APARECE SE FOR MATÉRIA */}
                      {modalType === 'materia' && (
                          <div>
                              <label className="block text-sm font-medium text-gray-700">
                                  Carga Horária Total (h/aula) *
                              </label>
                              <input 
                                  value={formData.carga_horaria || ''} 
                                  onChange={e => setFormData({...formData, carga_horaria: e.target.value})} 
                                  className="w-full border p-2 rounded" 
                                  required
                                  type="number"
                                  min="0"
                                  placeholder="Ex: 60" 
                              />
                              <p className="text-xs text-gray-500">Obrigatório para controle do QTS.</p>
                          </div>
                      )}

                      <div>
                          <label className="block text-sm font-medium text-gray-700">Vincular ao Curso *</label>
                          <select 
                              value={formData.curso_id || activeCursoTab || ''} 
                              onChange={e => setFormData({...formData, curso_id: e.target.value})} 
                              className="w-full border p-2 rounded bg-white"
                              required
                          >
                              <option value="">Selecione o Curso...</option>
                              {cursos.map(c => (
                                  <option key={c.id} value={c.id}>{c.nome}</option>
                              ))}
                          </select>
                      </div>
                  </>
              )}

              {error && (<div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">{error}</div>)}

              <div className="flex justify-end pt-4 border-t mt-4">
                  <Button type="submit" variant="primary" disabled={processing}>
                      {processing ? 'Salvando...' : (modalType === 'curso' ? 'Salvar Curso' : 'Salvar')}
                  </Button>
              </div>
          </form>
      </Modal>

      <Modal show={showConfirm} onClose={() => setShowConfirm(false)} title="Confirmar Exclusão" size="sm">
          <p>Tem certeza que deseja excluir este item?</p>
          {deleteAction?.type === 'curso' && <p className="text-xs text-red-600 mt-2">Certifique-se que o curso não possui itens vinculados antes de excluir.</p>}
          <div className="flex justify-end gap-2 mt-4"><Button onClick={() => setShowConfirm(false)} variant="secondary">Cancelar</Button><Button onClick={handleDelete} variant="danger" disabled={processing}>Excluir</Button></div>
      </Modal>
    </div>
  );
};